// ============================================================================
// CLIENT REGISTRATION HOOKS
// ============================================================================
// React hooks for client statutory registrations.
//
// Pattern follows useClientType.ts:
// - useClientRegistrations(params)   → list with pagination, refetch
// - useCreateClientRegistration()    → create mutation
// - useUpdateClientRegistration()    → update mutation
// - useDeleteClientRegistration()    → delete mutation
//
// `useClientRegistrations` accepts `skip` so the drill-down can mount before a
// client is chosen without firing an unscoped request that would pull every
// registration in the firm.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientRegistrationService } from 'api/services/clientRegistrationService';
import type {
  ID,
  PaginatedResponse,
  ClientRegistration,
  CreateClientRegistrationRequest,
  UpdateClientRegistrationRequest,
  ClientRegistrationSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of client registrations.
 * Refetches automatically when `params` changes.
 */
export const useClientRegistrations = (
  params?: ClientRegistrationSearchRequest,
  options?: { skip?: boolean }
) => {
  const skip = options?.skip ?? false;

  const [data, setData] = useState<ClientRegistration[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<ClientRegistration>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<Error | null>(null);

  // Serialize params so a new object reference each render doesn't re-fire the effect
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchClientRegistrations = useCallback(async () => {
    if (skip) {
      setData([]);
      setPagination(EMPTY_PAGINATION);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await clientRegistrationService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, skip]);

  useEffect(() => {
    fetchClientRegistrations();
  }, [fetchClientRegistrations]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchClientRegistrations,
  };
};

/**
 * Hook to create a new client registration.
 */
export const useCreateClientRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createClientRegistration = async (
    data: CreateClientRegistrationRequest,
    options?: {
      onSuccess?: (item: ClientRegistration) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await clientRegistrationService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createClientRegistration, loading, error, success };
};

/**
 * Hook to update a client registration. Always pass the latest rowVersion.
 */
export const useUpdateClientRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateClientRegistration = async (
    id: ID,
    data: UpdateClientRegistrationRequest,
    options?: {
      onSuccess?: (item: ClientRegistration) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await clientRegistrationService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateClientRegistration, loading, error, success };
};

/**
 * Hook to delete a client registration (soft-delete on the server).
 */
export const useDeleteClientRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteClientRegistration = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await clientRegistrationService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteClientRegistration, loading, error, success };
};
